import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { AuthProvider } from './shared/context/AuthContext.tsx';
import { EntitlementProvider } from './shared/context/EntitlementContext.tsx';
import { FeatureToggleProvider } from './shared/context/FeatureToggleContext.tsx';
import { DirectionProvider } from './shared/context/DirectionContext.tsx';
import App from './App.tsx';
import './index.css';
import './mobile.css';

Sentry.init({
  dsn: 'https://5df9f8df060ea2b589341a0b581deea3@o4511250777374720.ingest.us.sentry.io/4511250800902144',
  environment: import.meta.env.DEV ? 'development' : 'production',
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.05,
  replaysOnErrorSampleRate: 1.0,
  enabled: !import.meta.env.DEV,

  // Filter out noisy/expected errors
  beforeSend(event, hint) {
    const error = hint?.originalException;
    const message = typeof error === 'string' ? error : (error as Error)?.message || '';

    // Skip transient network errors
    const skipPatterns = [
      'Failed to fetch',
      'NetworkError',
      'Load failed',
      'net::ERR_',
      'AbortError',
      'Request timed out',
      'API route not available',
      'ResizeObserver loop',
      'Non-Error promise rejection',
      'ChunkLoadError',
      'Loading chunk',
      'dynamically imported module',
    ];

    if (skipPatterns.some(p => message.includes(p))) {
      return null; // Drop the event
    }

    // Skip browser extension errors
    if (event.exception?.values?.some(v => 
      v.stacktrace?.frames?.some(f => 
        f.filename?.includes('extension://') || 
        f.filename?.includes('chrome-extension') ||
        f.filename?.includes('moz-extension')
      )
    )) {
      return null;
    }

    return event;
  },

  // Clean up breadcrumbs
  beforeBreadcrumb(breadcrumb) {
    // Skip noisy console logs
    if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
      return null;
    }
    // Skip XHR breadcrumbs for health checks
    if (breadcrumb.category === 'xhr' && breadcrumb.data?.url?.includes('/api/health')) {
      return null;
    }
    return breadcrumb;
  },

  ignoreErrors: [
    // Browser/extension noise
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    'Non-Error promise rejection captured',
    // Network issues (user's connection)
    'TypeError: Failed to fetch',
    'TypeError: NetworkError when attempting to fetch resource',
    'TypeError: Load failed',
    // Chunk loading (user navigated away during load)
    /Loading chunk [\d]+ failed/,
    /dynamically imported module/,
  ],
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={({ error, resetError }) => (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-6">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Something went wrong</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            {(error as Error)?.message || 'An unexpected error occurred. Our team has been notified.'}
          </p>
          <div className="flex gap-3 justify-center">
            <button onClick={resetError}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
              Try Again
            </button>
            <button onClick={() => window.location.reload()}
              className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">
              Reload Page
            </button>
          </div>
          <p className="text-[10px] text-slate-400 mt-4">Error ID has been sent to our monitoring system</p>
        </div>
      </div>
    )}>
      <AuthProvider>
        <EntitlementProvider>
          <FeatureToggleProvider>
            <DirectionProvider>
              <App />
            </DirectionProvider>
          </FeatureToggleProvider>
        </EntitlementProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
